/**
 * CloudWatch RUM client singleton.
 *
 * Residency: the app monitor id and region come from the Lambda env via
 * next-runtime-env, and this module runs once per regional deployment. The
 * us-east-1 Lambda hands the browser a us-east-1 monitor and the browser
 * POSTs to dataplane.rum.us-east-1.amazonaws.com; eu-central-1 likewise.
 * There is no client-side region selection to get wrong.
 *
 * Authorization is a resource-based policy on the app monitor, not a Cognito
 * identity pool — hence `signing: false` and the `alias`. No AWS credentials
 * ever exist in the browser and there is no cognito-identity round trip on
 * page load.
 */

import { AwsRum, type AwsRumConfig } from "aws-rum-web";
import { getRuntimeConfig } from "@/lib/runtimeConfig";

let client: AwsRum | null = null;
let initialized = false;

/** Escape a string for literal use inside a RegExp. */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Initialise RUM, or return the existing instance.
 *
 * Guarded by a module-level flag rather than a React ref so that React 19
 * StrictMode's double-invocation, Fast Refresh, and multiple mount points
 * cannot produce two clients (which would double-count sessions and events).
 *
 * Returns null — deliberately, not throwing — when RUM is not configured.
 * An empty NEXT_PUBLIC_RUM_APP_MONITOR_ID is the kill switch: Terraform's
 * `rum_enabled = false` blanks it, so RUM turns off per environment with no
 * frontend change and no image rebuild.
 */
export function initRum(): AwsRum | null {
  if (initialized) {
    return client;
  }
  // SSR: never construct on the server. The client is browser-only and
  // reads window/document at construction.
  if (typeof window === "undefined") {
    return null;
  }

  initialized = true;

  const {
    rumAppMonitorId,
    rumRegion,
    rumAppVersion,
    rumAlias,
    rumSessionSampleRate,
    region,
  } = getRuntimeConfig();

  if (!rumAppMonitorId || !rumRegion) {
    return null;
  }

  // Only inject X-Amzn-Trace-Id into first-party requests. This is a
  // correctness requirement, not a preference: the header is a "non-simple"
  // header, so adding it to a cross-origin request forces a CORS preflight,
  // and if the target does not allow the header the *actual request fails* —
  // not just the trace. S3 presigned URLs, the CDN distributions, Cognito
  // and QuickSight are all cross-origin and must stay untouched.
  //
  // /api/* (API Gateway) and /graphql/* (AppSync) are both same-origin
  // behaviours on this app's own CloudFront distribution, so this one
  // pattern covers everything we actually want traced.
  const sameOrigin = new RegExp(
    `^${escapeRegExp(window.location.origin)}/`,
  );

  const config: AwsRumConfig = {
    sessionSampleRate: rumSessionSampleRate,

    // Sets cwr_s / cwr_u (anonymous uuid v4, unrelated to app identifiers).
    // Required for session counts, unique users and journey stitching.
    allowCookies: true,

    // Emit client-side X-Ray segments for sampled sessions.
    enableXRay: true,

    // Unsigned PutRumEvents; accepted because of the app monitor's
    // resource-based policy. Requires `alias` to match the policy's
    // rum:alias condition.
    signing: false,
    alias: rumAlias,

    // Resource URLs can carry identifiers; AWS explicitly recommends
    // disabling their collection when that is possible.
    recordResourceUrl: false,

    // Page views are recorded explicitly with route *templates* instead of
    // raw pathnames — see useRumPageViews. Without this the client would
    // also auto-record the raw pathname on every history change.
    disableAutoPageView: true,

    // Path only — keeps query strings out of page ids entirely, which
    // matters because /auth/callback carries the OAuth authorization code.
    pageIdFormat: "PATH",

    telemetries: [
      // JS errors + unhandled rejections.
      "errors",
      // Core Web Vitals, page load and navigation timing.
      "performance",
      // DOM interaction events, for navigation-path / user-journey views.
      "interaction",
      [
        "http",
        {
          // Record timing for successful requests too, not just failures —
          // otherwise there is no latency baseline to compare against.
          recordAllRequests: true,
          addXRayTraceIdHeader: [sameOrigin],
        },
      ],
      // NOTE: "replay" (rrweb full-DOM session replay) is deliberately NOT
      // enabled. It would capture rendered patient and case data verbatim.
      // It must not be turned on without a privacy/legal review.
    ],
  };

  try {
    client = new AwsRum(rumAppMonitorId, rumAppVersion, rumRegion, config);

    // The deploy region doubles as the residency dimension: it lets you
    // assert in the RUM console that no eu-central-1 session ever appears
    // in the us-east-1 monitor.
    client.addSessionAttributes({ awsRegion: region || rumRegion });
  } catch (error) {
    // Never let telemetry break the app. A misconfigured monitor, a blocked
    // request or a hostile browser extension must not take down the page.
    client = null;
    console.error("[rum] failed to initialize CloudWatch RUM", error);
  }

  return client;
}

/** The RUM client, or null when RUM is disabled or failed to initialise. */
export const getRum = (): AwsRum | null => client;

/**
 * Record an error through RUM if it is available.
 * Safe to call from anywhere, including before initialisation and on the server.
 */
export function recordRumError(error: unknown): void {
  try {
    client?.recordError(error);
  } catch {
    // swallow — telemetry must never mask the original error
  }
}
